import React from "react";
import { Link } from "react-router-dom";
import {
  Database,
  ShieldCheck,
  Bell,
  Lightning,
  Eye,
  Users,
  HandHeart,
  Buildings,
  Tree,
  ClipboardText,
  MapTrifold,
  ChartLineUp,
  CheckCircle,
  X as CloseIcon,
  EnvelopeSimple,
  WhatsappLogo,
  PaperPlaneTilt,
  ChatsTeardrop,
  ListChecks,
  CloudArrowUp,
  Sparkle,
  LockKey,
  Briefcase,
  Wrench,
  ArrowRight,
  Target,
  Newspaper,
  FileText,
  MapPin,
  Compass,
  GlobeHemisphereWest,
  Mountains,
  ChatCircleText,
  GraduationCap,
  Heartbeat,
  PaintBrush,
  SoccerBall,
  Crosshair,
  CaretRight,
} from "@phosphor-icons/react";

// =================================================================
// Página de Brochures para LinkedIn — formatos cuadrados 1080x1080
// =================================================================

const SIZE = 1080;

function BrochureFrame({ id, children, bg = "default" }) {
  const bgClass = {
    default: "bg-gradient-to-br from-slate-950 via-[#1a0f08] to-slate-950",
    orange: "bg-gradient-to-br from-brand-orange-600 via-brand-orange-500 to-amber-600",
    green: "bg-gradient-to-br from-emerald-700 via-brand-green-600 to-emerald-800",
    dark: "bg-[#0a0a0a]",
    light: "bg-gradient-to-br from-orange-50 via-white to-emerald-50",
  }[bg];

  return (
    <div
      id={id}
      data-brochure={id}
      className={`relative overflow-hidden text-white shadow-2xl ${bgClass}`}
      style={{ width: SIZE, height: SIZE }}
    >
      {/* Grain pattern */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none mix-blend-screen"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
          backgroundSize: "32px 32px",
        }}
      />
      {/* Top branding bar - prominent */}
      <div className="absolute top-0 left-0 right-0 h-[88px] px-14 flex items-center z-20 border-b border-white/10 bg-black/20 backdrop-blur-sm">
        <div className="flex items-center gap-4">
          <div className="w-2 h-10 rounded-full bg-brand-orange-400" />
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-brand-orange-300 font-bold">Software</div>
            <div className="font-display text-[22px] font-bold tracking-tight text-white leading-tight">Gestión Social y Ambiental</div>
          </div>
        </div>
      </div>
      {/* Bottom branding bar - fixed height 140px */}
      <div className="absolute bottom-0 left-0 right-0 h-[140px] px-14 flex items-center justify-between z-20 border-t border-white/5">
        <img
          src="/assets/sigsaop-logo-light.svg"
          alt="SIGSAOP · Software de Gestión Social y Ambiental"
          className="h-[92px] w-auto"
          onError={(e) => { e.target.style.display = "none"; }}
        />
        <div className="text-right">
          <div className="text-[22px] font-display font-bold text-white tracking-wide">sigsaop.cl</div>
          <div className="text-[12px] uppercase tracking-[0.25em] text-white/60 mt-1">Software · Gestión social y ambiental</div>
        </div>
      </div>
      {/* Content */}
      <div className="relative z-10 w-full h-full">{children}</div>
    </div>
  );
}

function Pill({ children, tone = "orange" }) {
  const tones = {
    orange: "border-brand-orange-400/50 bg-brand-orange-500/20 text-brand-orange-200",
    green: "border-brand-green-400/50 bg-brand-green-500/20 text-brand-green-200",
    white: "border-white/40 bg-white/15 text-white",
  };
  return (
    <span className={`inline-flex items-center gap-2 rounded-full border ${tones[tone]} px-4 py-1.5 text-[11px] uppercase tracking-[0.25em] font-semibold backdrop-blur-sm`}>
      <Sparkle size={12} weight="fill" /> {children}
    </span>
  );
}

// =================================================================
// 01 — Hook question
// =================================================================
function BrochureCover() {
  return (
    <BrochureFrame id="b01-cover">
      <div className="absolute -top-20 -right-20 w-[600px] h-[600px] rounded-full bg-brand-orange-500/35 blur-3xl" />
      <div className="absolute -bottom-40 -left-32 w-[600px] h-[600px] rounded-full bg-brand-green-500/20 blur-3xl" />
      <div className="relative h-full flex flex-col justify-between px-16 pt-[108px] pb-[160px]">
        <div>
          <h1 className="font-display text-[88px] leading-[0.95] font-bold mt-6 tracking-tight">
            ¿Tu gestión<br />social y ambiental<br /><span className="text-brand-orange-400">vive en Excel<br />o Power Point?</span>
          </h1>
        </div>
        <div>
          {/* Domain chips infographic */}
          <div className="flex flex-wrap gap-2.5 mb-7">
            {[
              { icon: HandHeart, label: "Comunidades", tone: "green" },
              { icon: Tree, label: "RCA / Ambiental", tone: "green" },
              { icon: Buildings, label: "Autoridades", tone: "orange" },
              { icon: ListChecks, label: "Compromisos", tone: "orange" },
              { icon: Newspaper, label: "Parlamentarios", tone: "orange" },
              { icon: Briefcase, label: "Proyectos", tone: "sky" },
            ].map((c) => {
              const tones = {
                green: "border-brand-green-400/40 bg-brand-green-500/15 text-brand-green-200",
                orange: "border-brand-orange-400/40 bg-brand-orange-500/15 text-brand-orange-200",
                sky: "border-sky-400/40 bg-sky-500/15 text-sky-200",
              };
              return (
                <div key={c.label} className={`inline-flex items-center gap-2 rounded-full border ${tones[c.tone]} px-4 py-2`}>
                  <c.icon size={18} weight="duotone" />
                  <span className="text-[14px] font-semibold">{c.label}</span>
                </div>
              );
            })}
          </div>
          <p className="text-[22px] text-slate-100 leading-snug max-w-[920px]">
            <strong className="text-white">SIGSAOP</strong>, la plataforma que <strong className="text-white">reemplaza tus planillas</strong> y te devuelve el control de tu gestión social y ambiental.
          </p>
        </div>
      </div>
    </BrochureFrame>
  );
}

// =================================================================
// 02 — De Antofagasta al mundo (local roots)
// =================================================================
function BrochureAntofagasta() {
  return (
    <BrochureFrame id="b02-antofagasta">
      <div className="absolute -top-32 -left-32 w-[600px] h-[600px] rounded-full bg-brand-orange-500/25 blur-3xl" />
      <div className="absolute -bottom-40 -right-20 w-[600px] h-[600px] rounded-full bg-amber-500/15 blur-3xl" />
      <div className="px-16 pt-[108px] pb-[160px] h-full flex flex-col">
        <Pill>Origen · Nuestra región</Pill>
        <h2 className="font-display text-[88px] leading-[0.95] font-bold mt-6 max-w-[920px]">
          De <span className="text-brand-orange-400">Antofagasta</span><br />al mundo.
        </h2>

        {/* Infografía: planeta con flechas radiando desde Antofagasta */}
        <div className="mt-auto grid grid-cols-12 gap-6 items-center">
          {/* Globo */}
          <div className="col-span-7 flex justify-center">
            <svg viewBox="0 0 500 500" className="w-full max-w-[460px] h-auto">
              <defs>
                <radialGradient id="globeBg" cx="40%" cy="35%">
                  <stop offset="0%" stopColor="#1e3a8a" stopOpacity="0.6" />
                  <stop offset="60%" stopColor="#0f172a" stopOpacity="0.95" />
                  <stop offset="100%" stopColor="#020617" />
                </radialGradient>
                <radialGradient id="globeHL" cx="35%" cy="30%">
                  <stop offset="0%" stopColor="#85c536" stopOpacity="0.35" />
                  <stop offset="60%" stopColor="#85c536" stopOpacity="0" />
                </radialGradient>
                <filter id="glow">
                  <feGaussianBlur stdDeviation="3" result="b" />
                  <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
                </filter>
              </defs>

              {/* Globe sphere */}
              <circle cx="250" cy="250" r="170" fill="url(#globeBg)" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" />
              <circle cx="250" cy="250" r="170" fill="url(#globeHL)" />

              {/* Latitude lines (ellipses) */}
              <ellipse cx="250" cy="250" rx="170" ry="40" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="0.8" />
              <ellipse cx="250" cy="250" rx="170" ry="85" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="0.8" />
              <ellipse cx="250" cy="250" rx="170" ry="130" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="0.8" />

              {/* Longitude lines (ellipses) */}
              <ellipse cx="250" cy="250" rx="40" ry="170" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="0.8" />
              <ellipse cx="250" cy="250" rx="85" ry="170" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="0.8" />
              <ellipse cx="250" cy="250" rx="130" ry="170" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="0.8" />

              {/* Continent dots pattern (stylized) */}
              <g fill="rgba(133, 197, 54, 0.45)">
                {/* South America rough cluster around Antofagasta */}
                <circle cx="215" cy="305" r="2.5" />
                <circle cx="222" cy="295" r="2.5" />
                <circle cx="218" cy="320" r="2.5" />
                <circle cx="208" cy="335" r="2.5" />
                <circle cx="225" cy="345" r="2.5" />
                <circle cx="212" cy="355" r="2.5" />
                <circle cx="232" cy="318" r="2.5" />
                {/* North America */}
                <circle cx="180" cy="200" r="2.5" />
                <circle cx="195" cy="210" r="2.5" />
                <circle cx="170" cy="220" r="2.5" />
                <circle cx="190" cy="235" r="2.5" />
                <circle cx="210" cy="225" r="2.5" />
                {/* Europe */}
                <circle cx="300" cy="195" r="2.5" />
                <circle cx="315" cy="205" r="2.5" />
                <circle cx="295" cy="215" r="2.5" />
                {/* Africa */}
                <circle cx="288" cy="260" r="2.5" />
                <circle cx="300" cy="280" r="2.5" />
                <circle cx="295" cy="300" r="2.5" />
                <circle cx="280" cy="320" r="2.5" />
                {/* Asia */}
                <circle cx="345" cy="220" r="2.5" />
                <circle cx="360" cy="240" r="2.5" />
                <circle cx="340" cy="260" r="2.5" />
                {/* Oceania */}
                <circle cx="365" cy="330" r="2.5" />
                <circle cx="375" cy="345" r="2.5" />
              </g>

              {/* Origin pin: Antofagasta */}
              <g filter="url(#glow)">
                <circle cx="218" cy="320" r="11" fill="#ff751f" />
                <circle cx="218" cy="320" r="5" fill="#fff" />
              </g>

              {/* Arrows radiating from Antofagasta */}
              {[
                { x: 95, y: 110, label: "Norte" },
                { x: 410, y: 90, label: "Europa" },
                { x: 470, y: 200, label: "Asia" },
                { x: 430, y: 400, label: "Oceanía" },
                { x: 380, y: 460, label: "África" },
              ].map((d, i) => {
                // curved path from antofagasta (218,320) to destination
                const cx = (218 + d.x) / 2 + (d.y < 250 ? -40 : 30);
                const cy = (320 + d.y) / 2 - (d.x > 250 ? 60 : 40);
                return (
                  <g key={i}>
                    <path
                      d={`M 218 320 Q ${cx} ${cy} ${d.x} ${d.y}`}
                      fill="none"
                      stroke="#ff751f"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeDasharray="5 5"
                      opacity="0.9"
                    />
                    <circle cx={d.x} cy={d.y} r="6" fill="#ff751f" />
                    <circle cx={d.x} cy={d.y} r="2" fill="#fff" />
                  </g>
                );
              })}

              {/* Antofagasta label */}
              <g>
                <rect x="135" y="345" width="125" height="28" rx="14" fill="#ff751f" />
                <text x="197.5" y="364" textAnchor="middle" fontSize="14" fontWeight="800" fill="#fff" fontFamily="system-ui">ANTOFAGASTA</text>
              </g>
            </svg>
          </div>

          {/* 3 ventajas locales - solo títulos */}
          <div className="col-span-5 grid gap-3.5">
            {[
              { icon: Mountains, t: "Hechos para el desierto" },
              { icon: ChatCircleText, t: "Cercanía real" },
              { icon: GlobeHemisphereWest, t: "Escalable al mundo" },
            ].map((r) => (
              <div key={r.t} className="flex items-center gap-4 rounded-xl border border-white/10 bg-white/[0.04] px-5 py-4">
                <div className="w-12 h-12 rounded-lg bg-brand-orange-500/20 border border-brand-orange-400/30 flex items-center justify-center flex-shrink-0">
                  <r.icon size={24} weight="duotone" className="text-brand-orange-300" />
                </div>
                <div className="font-display text-[19px] font-bold text-white leading-tight">{r.t}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </BrochureFrame>
  );
}

// =================================================================
// 03 — Sabemos cómo gestionar comunidades (expertise)
// =================================================================
function BrochureExperiencia() {
  return (
    <BrochureFrame id="b03-experiencia">
      <div className="absolute -top-20 -right-20 w-[600px] h-[600px] rounded-full bg-brand-green-500/20 blur-3xl" />
      <div className="px-16 pt-[108px] pb-[160px] h-full flex flex-col">
        <Pill>Experiencia · Conocimiento</Pill>
        <h2 className="font-display text-[80px] leading-[0.95] font-bold mt-8 max-w-[920px]">
          Sabemos cómo<br /><span className="text-brand-green-400">gestionar comunidades</span>.
        </h2>
        <p className="text-[22px] text-slate-200 mt-7 max-w-[850px] leading-snug">
          No somos una empresa de software genérica. Diseñamos SIGSAOP junto a profesionales que viven la gestión social y ambiental en terreno.
        </p>

        {/* 4 pilares de expertise */}
        <div className="grid grid-cols-2 gap-5 mt-auto">
          {[
            { icon: HandHeart, t: "Relación comunitaria", d: "Diálogo continuo, registro de cada conversación y acuerdos vivos.", tone: "green" },
            { icon: Tree, t: "Cumplimiento ambiental", d: "RCA, compromisos voluntarios, indicadores y evidencia auditable.", tone: "green" },
            { icon: Buildings, t: "Asuntos públicos", d: "Autoridades, parlamentarios, gremios y agenda institucional.", tone: "orange" },
            { icon: GraduationCap, t: "Metodología propia", d: "Años de práctica con mineras, municipios y consultoras del norte.", tone: "sky" },
          ].map((p) => {
            const tones = {
              green: { border: "border-brand-green-400/40", bg: "from-brand-green-500/15", icon: "text-brand-green-300", iconBg: "bg-brand-green-500/20 border-brand-green-400/30" },
              orange: { border: "border-brand-orange-400/40", bg: "from-brand-orange-500/15", icon: "text-brand-orange-300", iconBg: "bg-brand-orange-500/20 border-brand-orange-400/30" },
              sky: { border: "border-sky-400/40", bg: "from-sky-500/15", icon: "text-sky-300", iconBg: "bg-sky-500/20 border-sky-400/30" },
            }[p.tone];
            return (
              <div key={p.t} className={`rounded-2xl border ${tones.border} bg-gradient-to-br ${tones.bg} to-transparent p-6`}>
                <div className={`w-14 h-14 rounded-xl ${tones.iconBg} border flex items-center justify-center mb-4`}>
                  <p.icon size={28} weight="duotone" className={tones.icon} />
                </div>
                <div className="font-display text-2xl font-bold text-white">{p.t}</div>
                <div className="text-[15px] text-slate-300 mt-2 leading-relaxed">{p.d}</div>
              </div>
            );
          })}
        </div>
      </div>
    </BrochureFrame>
  );
}

// =================================================================
// 04 — El problema
// =================================================================
function BrochureProblema() {
  return (
    <BrochureFrame id="b02-problema">
      <div className="absolute -top-20 -right-20 w-[500px] h-[500px] rounded-full bg-rose-500/15 blur-3xl" />
      <div className="px-16 pt-[108px] pb-[160px] h-full flex flex-col">
        <Pill>El costo de seguir igual</Pill>
        <h2 className="font-display text-[72px] leading-[0.95] font-bold mt-10 max-w-[900px]">
          Un compromiso olvidado <span className="text-rose-400">cuesta más</span> que la plataforma.
        </h2>
        <p className="text-[22px] text-slate-300 mt-8 max-w-[820px] leading-snug">
          En gestión social y ambiental, lo que no se registra no se cumple. Multas, RCA observada, denuncias y reputación dañada.
        </p>
        <div className="grid grid-cols-3 gap-5 mt-auto pb-4">
          {[
            { stat: "1×", label: "RCA incumplida", sub: "Puede significar millones en multas y paralizaciones." },
            { stat: "48%", label: "Vence sin aviso", sub: "Casi la mitad de los plazos se cumplen tarde." },
            { stat: "0", label: "Memoria al renunciar", sub: "El que sabía se fue, y se llevó el Excel." },
          ].map((s) => (
            <div key={s.label} className="rounded-2xl border border-rose-400/30 bg-gradient-to-br from-rose-500/15 to-transparent p-6">
              <div className="font-display text-6xl font-bold text-rose-300 leading-none">{s.stat}</div>
              <div className="text-xs uppercase tracking-[0.18em] text-white mt-4 font-bold">{s.label}</div>
              <div className="text-[13px] text-slate-400 mt-2 leading-relaxed">{s.sub}</div>
            </div>
          ))}
        </div>
      </div>
    </BrochureFrame>
  );
}

// =================================================================
// 03 — 10 módulos
// =================================================================
function BrochureModulos() {
  const modules = [
    { icon: Tree, label: "RCA / Ambiental", tone: "green" },
    { icon: HandHeart, label: "Comunidades", tone: "green" },
    { icon: Buildings, label: "Asuntos Públicos", tone: "orange" },
    { icon: Newspaper, label: "Parlamentarios", tone: "orange" },
    { icon: ChatsTeardrop, label: "Reuniones", tone: "violet" },
    { icon: Briefcase, label: "Proyectos", tone: "sky" },
    { icon: ListChecks, label: "Compromisos", tone: "orange" },
    { icon: Target, label: "Indicadores", tone: "rose" },
    { icon: MapTrifold, label: "Mapa territorial", tone: "sky" },
    { icon: ChartLineUp, label: "Reportería", tone: "green" },
  ];
  const tones = {
    orange: "border-brand-orange-400/30 bg-brand-orange-500/10 text-brand-orange-300",
    green: "border-brand-green-400/30 bg-brand-green-500/10 text-brand-green-300",
    sky: "border-sky-400/30 bg-sky-500/10 text-sky-300",
    violet: "border-violet-400/30 bg-violet-500/10 text-violet-300",
    rose: "border-rose-400/30 bg-rose-500/10 text-rose-300",
  };
  return (
    <BrochureFrame id="b03-modulos">
      <div className="px-16 pt-[108px] pb-[160px] h-full flex flex-col">
        <Pill>Una sola plataforma</Pill>
        <h2 className="font-display text-[72px] leading-[0.95] font-bold mt-10 max-w-[900px]">
          Reemplaza <span className="text-rose-400">10 Excel</span><br />por <span className="text-brand-orange-400">una sola pantalla</span>.
        </h2>
        <p className="text-[22px] text-slate-300 mt-8 max-w-[850px] leading-snug">
          Toda tu gestión social, ambiental y de asuntos públicos — conectada, trazable y al alcance de tu equipo en segundos.
        </p>
        <div className="grid grid-cols-5 gap-4 mt-auto pb-4">
          {modules.map((m) => (
            <div key={m.label} className={`rounded-2xl border ${tones[m.tone]} p-5 flex flex-col items-center justify-center gap-3 text-center min-h-[150px]`}>
              <m.icon size={42} weight="duotone" />
              <div className="text-[14px] font-semibold text-white leading-tight">{m.label}</div>
            </div>
          ))}
        </div>
      </div>
    </BrochureFrame>
  );
}

// =================================================================
// Dashboard ejecutivo
// =================================================================
function BrochureDashboard() {
  const kpis = [
    { stat: "22", label: "Comunidades", tone: "green", icon: HandHeart },
    { stat: "47", label: "Compromisos", tone: "orange", icon: ListChecks },
    { stat: "$2.400M", label: "Inversión social", tone: "sky", icon: ChartLineUp },
    { stat: "92%", label: "Cumplimiento", tone: "violet", icon: Target },
  ];
  const toneMap = {
    green: { border: "border-brand-green-400/40", bg: "from-brand-green-500/15", stat: "text-brand-green-300", icon: "text-brand-green-400" },
    orange: { border: "border-brand-orange-400/40", bg: "from-brand-orange-500/15", stat: "text-brand-orange-300", icon: "text-brand-orange-400" },
    sky: { border: "border-sky-400/40", bg: "from-sky-500/15", stat: "text-sky-300", icon: "text-sky-400" },
    violet: { border: "border-violet-400/40", bg: "from-violet-500/15", stat: "text-violet-300", icon: "text-violet-400" },
  };

  return (
    <BrochureFrame id="b07-dashboard" bg="dark">
      <div className="absolute -top-32 -right-20 w-[500px] h-[500px] rounded-full bg-brand-orange-500/15 blur-3xl" />
      <div className="px-16 pt-[108px] pb-[160px] h-full flex flex-col">
        <Pill>Dashboard ejecutivo</Pill>
        <h2 className="font-display text-[72px] leading-[0.95] font-bold mt-8 max-w-[940px]">
          Toda tu operación en <span className="text-brand-orange-400">una sola pantalla</span>.
        </h2>
        <p className="text-[22px] text-slate-200 mt-7 max-w-[900px] leading-snug">
          Visión consolidada de comunidades, autoridades, proyectos, compromisos e inversión social y ambiental.
        </p>

        {/* Infografía: dashboard mockup */}
        <div className="mt-auto rounded-2xl border border-white/15 bg-gradient-to-br from-slate-900/80 to-slate-800/40 p-5 space-y-4">
          {/* Header del dashboard */}
          <div className="flex items-center justify-between border-b border-white/10 pb-3">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full bg-rose-500" />
              <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
              <div className="w-2.5 h-2.5 rounded-full bg-brand-green-500" />
            </div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-white/60 font-bold">Vista consolidada</div>
          </div>

          {/* 4 KPI cards */}
          <div className="grid grid-cols-4 gap-3">
            {kpis.map((k) => {
              const t = toneMap[k.tone];
              return (
                <div key={k.label} className={`rounded-xl border ${t.border} bg-gradient-to-br ${t.bg} to-transparent p-3.5`}>
                  <k.icon size={20} weight="duotone" className={`${t.icon} mb-2`} />
                  <div className={`font-display text-3xl font-bold ${t.stat} leading-none`}>{k.stat}</div>
                  <div className="text-[10px] uppercase tracking-wider text-white/80 mt-2 font-semibold">{k.label}</div>
                </div>
              );
            })}
          </div>

          {/* 2 chart cards */}
          <div className="grid grid-cols-2 gap-3">
            {/* Bar chart - Compromisos por mes */}
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3.5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] uppercase tracking-wider text-white font-semibold">Cumplimiento mensual</span>
                <span className="text-[10px] text-brand-green-300">↑ 18%</span>
              </div>
              <svg viewBox="0 0 200 60" className="w-full h-[60px]">
                {[24, 36, 28, 42, 38, 50, 46, 56].map((h, i) => (
                  <rect key={i} x={i * 24 + 4} y={60 - h} width="16" height={h}
                    fill="url(#barG)" rx="2" />
                ))}
                <defs>
                  <linearGradient id="barG" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ff751f" />
                    <stop offset="100%" stopColor="#ff751f" stopOpacity="0.3" />
                  </linearGradient>
                </defs>
              </svg>
            </div>

            {/* Line chart - Inversión */}
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3.5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] uppercase tracking-wider text-white font-semibold">Inversión acumulada</span>
                <span className="text-[10px] text-brand-orange-300">YTD</span>
              </div>
              <svg viewBox="0 0 200 60" className="w-full h-[60px]">
                <defs>
                  <linearGradient id="lineG" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#85c536" stopOpacity="0.4" />
                    <stop offset="100%" stopColor="#85c536" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path d="M 0 50 L 25 42 L 50 38 L 75 28 L 100 25 L 125 20 L 150 14 L 175 10 L 200 6 L 200 60 L 0 60 Z" fill="url(#lineG)" />
                <path d="M 0 50 L 25 42 L 50 38 L 75 28 L 100 25 L 125 20 L 150 14 L 175 10 L 200 6" fill="none" stroke="#85c536" strokeWidth="2.5" strokeLinecap="round" />
                {[[0,50],[25,42],[50,38],[75,28],[100,25],[125,20],[150,14],[175,10],[200,6]].map((p,i) => (
                  <circle key={i} cx={p[0]} cy={p[1]} r="2.5" fill="#85c536" />
                ))}
              </svg>
            </div>
          </div>
        </div>
      </div>
    </BrochureFrame>
  );
}

// =================================================================
// Proyectos
// =================================================================
function BrochureProyectos() {
  return (
    <BrochureFrame id="b08-proyectos">
      <div className="absolute -top-32 -right-20 w-[500px] h-[500px] rounded-full bg-sky-500/15 blur-3xl" />
      <div className="px-16 pt-[108px] pb-[160px] h-full flex flex-col">
        <Pill>Proyectos</Pill>
        <h2 className="font-display text-[72px] leading-[0.95] font-bold mt-8 max-w-[940px]">
          Cada iniciativa con <span className="text-brand-orange-400">huella medible</span>.
        </h2>
        <p className="text-[22px] text-slate-200 mt-7 max-w-[900px] leading-snug">
          Iniciativas con eje estratégico, inversión, ejecución y beneficiarios — todo en una ficha trazable.
        </p>

        {/* Infografía: tarjeta de proyecto destacada + mini-tarjetas */}
        <div className="mt-auto space-y-4">
          {/* Proyecto destacado */}
          <div className="rounded-2xl border-2 border-brand-orange-400/50 bg-gradient-to-br from-brand-orange-500/15 to-transparent p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-brand-orange-300 font-bold">Proyecto destacado</div>
                <div className="font-display text-2xl font-bold text-white mt-1">Programa de capacitación · Mejillones</div>
              </div>
              <div className="inline-flex items-center gap-1.5 rounded-full bg-brand-green-500/20 border border-brand-green-400/40 px-3 py-1.5">
                <GraduationCap size={14} weight="duotone" className="text-brand-green-300" />
                <span className="text-[11px] uppercase tracking-wider text-brand-green-200 font-bold">Educacional</span>
              </div>
            </div>
            {/* 4 dimensiones */}
            <div className="grid grid-cols-4 gap-3">
              <div className="rounded-lg bg-white/[0.05] border border-white/10 px-3 py-3">
                <div className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">Eje estratégico</div>
                <div className="flex items-center gap-1.5 mt-1.5">
                  <Crosshair size={16} weight="duotone" className="text-brand-orange-400" />
                  <span className="text-[14px] font-bold text-white">Educación</span>
                </div>
              </div>
              <div className="rounded-lg bg-white/[0.05] border border-white/10 px-3 py-3">
                <div className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">Inversión</div>
                <div className="font-display text-xl font-bold text-brand-orange-300 mt-1">$180M</div>
              </div>
              <div className="rounded-lg bg-white/[0.05] border border-white/10 px-3 py-3">
                <div className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">Ejecución</div>
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full w-[68%] bg-gradient-to-r from-brand-orange-500 to-brand-green-500 rounded-full" />
                  </div>
                  <span className="text-[12px] font-bold text-brand-green-300">68%</span>
                </div>
              </div>
              <div className="rounded-lg bg-white/[0.05] border border-white/10 px-3 py-3">
                <div className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">Beneficiarios</div>
                <div className="flex items-center gap-1.5 mt-1.5">
                  <Users size={16} weight="duotone" className="text-brand-green-400" />
                  <span className="font-display text-xl font-bold text-white">412</span>
                </div>
              </div>
            </div>
          </div>

          {/* Mini tarjetas de otros proyectos */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { eje: "Salud", color: "rose", inv: "$95M", prog: 92 },
              { eje: "Cultural", color: "violet", inv: "$48M", prog: 34 },
              { eje: "Ambiental", color: "green", inv: "$230M", prog: 76 },
            ].map((p) => {
              const tones = {
                rose: { border: "border-rose-400/30", bg: "bg-rose-500/10", text: "text-rose-300", bar: "from-rose-500 to-rose-400" },
                violet: { border: "border-violet-400/30", bg: "bg-violet-500/10", text: "text-violet-300", bar: "from-violet-500 to-violet-400" },
                green: { border: "border-brand-green-400/30", bg: "bg-brand-green-500/10", text: "text-brand-green-300", bar: "from-brand-green-500 to-brand-green-400" },
              }[p.color];
              return (
                <div key={p.eje} className={`rounded-xl border ${tones.border} ${tones.bg} p-4`}>
                  <div className={`text-[10px] uppercase tracking-wider ${tones.text} font-bold`}>{p.eje}</div>
                  <div className="font-display text-2xl font-bold text-white mt-1">{p.inv}</div>
                  <div className="mt-3 h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <div className={`h-full bg-gradient-to-r ${tones.bar} rounded-full`} style={{ width: `${p.prog}%` }} />
                  </div>
                  <div className="text-[10px] text-white/70 mt-1.5">{p.prog}% ejecutado</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </BrochureFrame>
  );
}

// =================================================================
// Mapa territorial
// =================================================================
function BrochureMapa() {
  // Pin positions on a stylized map (percentage based)
  const pins = [
    { x: 25, y: 30, icon: HandHeart, tone: "green", label: "Comunidad" },
    { x: 55, y: 22, icon: Buildings, tone: "orange", label: "Autoridad" },
    { x: 72, y: 48, icon: MapTrifold, tone: "sky", label: "GORE" },
    { x: 38, y: 58, icon: Newspaper, tone: "violet", label: "Organización" },
    { x: 18, y: 70, icon: HandHeart, tone: "green", label: "Comunidad" },
    { x: 64, y: 75, icon: Buildings, tone: "orange", label: "Autoridad" },
  ];
  const toneMap = {
    green: { bg: "bg-brand-green-500", ring: "ring-brand-green-400/50" },
    orange: { bg: "bg-brand-orange-500", ring: "ring-brand-orange-400/50" },
    sky: { bg: "bg-sky-500", ring: "ring-sky-400/50" },
    violet: { bg: "bg-violet-500", ring: "ring-violet-400/50" },
  };

  return (
    <BrochureFrame id="b07-mapa">
      <div className="absolute -top-20 -right-20 w-[500px] h-[500px] rounded-full bg-sky-500/15 blur-3xl" />
      <div className="px-16 pt-[108px] pb-[160px] h-full flex flex-col">
        <Pill>Mapa territorial</Pill>
        <h2 className="font-display text-[72px] leading-[0.95] font-bold mt-8 max-w-[940px]">
          Tu territorio, <span className="text-brand-orange-400">en un solo mapa</span>.
        </h2>
        <p className="text-[22px] text-slate-200 mt-7 max-w-[900px] leading-snug">
          Geolocaliza comunidades, autoridades, GORE y organizaciones. Haz click en un marcador y abre la ficha completa, directorio y entidades vinculadas.
        </p>

        {/* Infografía: mapa simulado + panel de detalle */}
        <div className="mt-auto grid grid-cols-12 gap-5">
          {/* Mapa simulado */}
          <div className="col-span-7 relative rounded-2xl border border-white/15 bg-gradient-to-br from-slate-800/60 to-slate-900/60 overflow-hidden h-[360px]">
            {/* Grid lines for map feel */}
            <div className="absolute inset-0 opacity-30" style={{
              backgroundImage: "linear-gradient(to right, rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.08) 1px, transparent 1px)",
              backgroundSize: "40px 40px",
            }} />
            {/* Coast line decorative */}
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              <path d="M 5 10 Q 20 25, 15 40 T 25 70 Q 30 85, 45 90 L 80 95 L 95 80 Q 90 60, 95 40 L 85 15 Q 70 5, 50 10 Z"
                fill="rgba(132, 204, 22, 0.06)" stroke="rgba(132, 204, 22, 0.25)" strokeWidth="0.3" />
            </svg>
            {/* Pins */}
            {pins.map((p, i) => {
              const t = toneMap[p.tone];
              return (
                <div
                  key={i}
                  className={`absolute -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full ${t.bg} ring-4 ${t.ring} flex items-center justify-center shadow-xl`}
                  style={{ left: `${p.x}%`, top: `${p.y}%` }}
                >
                  <p.icon size={20} weight="fill" className="text-white" />
                </div>
              );
            })}
            {/* Legend */}
            <div className="absolute bottom-3 left-3 right-3 flex flex-wrap gap-2">
              {[
                { label: "Comunidades", tone: "green" },
                { label: "Autoridades", tone: "orange" },
                { label: "GORE", tone: "sky" },
                { label: "Organizaciones", tone: "violet" },
              ].map((l) => {
                const t = toneMap[l.tone];
                return (
                  <div key={l.label} className="inline-flex items-center gap-2 rounded-full bg-black/50 backdrop-blur-sm border border-white/10 px-3 py-1.5">
                    <div className={`w-2.5 h-2.5 rounded-full ${t.bg}`} />
                    <span className="text-[11px] uppercase tracking-wider text-white font-semibold">{l.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Panel de detalle al click */}
          <div className="col-span-5 rounded-2xl border-2 border-brand-orange-400/50 bg-gradient-to-br from-brand-orange-500/15 to-transparent p-5 flex flex-col">
            <div className="text-[10px] uppercase tracking-[0.25em] text-brand-orange-300 font-bold">Click en marcador</div>
            <div className="font-display text-2xl font-bold text-white mt-1 leading-tight">Comunidad<br />ejemplo</div>
            <div className="mt-4 space-y-2.5 text-[14px] text-slate-200 flex-1">
              {[
                "Ficha completa",
                "Directorio de autoridades",
                "Entidades vinculadas",
                "Compromisos activos",
                "Reuniones registradas",
              ].map((t) => (
                <div key={t} className="flex items-center gap-2.5">
                  <CaretRight size={14} weight="bold" className="text-brand-orange-400 flex-shrink-0" />
                  <span>{t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </BrochureFrame>
  );
}

// =================================================================
// Metas Corporativas — Asuntos Públicos
// =================================================================
function BrochureMetasCorporativas() {
  const ejes = [
    { icon: HandHeart, label: "Social", tone: "green" },
    { icon: GraduationCap, label: "Educacional", tone: "sky" },
    { icon: PaintBrush, label: "Cultural", tone: "violet" },
    { icon: SoccerBall, label: "Deportivo", tone: "orange" },
    { icon: Tree, label: "Ambiental", tone: "green" },
    { icon: Heartbeat, label: "Salud", tone: "rose" },
    { icon: Buildings, label: "Empleo local", tone: "orange" },
    { icon: Newspaper, label: "Asuntos públicos", tone: "sky" },
  ];
  const tones = {
    orange: "border-brand-orange-400/40 bg-brand-orange-500/15 text-brand-orange-300",
    green: "border-brand-green-400/40 bg-brand-green-500/15 text-brand-green-300",
    sky: "border-sky-400/40 bg-sky-500/15 text-sky-300",
    violet: "border-violet-400/40 bg-violet-500/15 text-violet-300",
    rose: "border-rose-400/40 bg-rose-500/15 text-rose-300",
  };
  return (
    <BrochureFrame id="b06-metas-corporativas">
      <div className="absolute -top-20 -right-20 w-[600px] h-[600px] rounded-full bg-brand-orange-500/20 blur-3xl" />
      <div className="px-16 pt-[108px] pb-[160px] h-full flex flex-col">
        <Pill>Metas corporativas · Asuntos públicos</Pill>
        <h2 className="font-display text-[72px] leading-[0.95] font-bold mt-8 max-w-[940px]">
          Lleva tus <span className="text-brand-orange-400">metas corporativas</span> al cumplimiento.
        </h2>
        <p className="text-[22px] text-slate-200 mt-7 max-w-[900px] leading-snug">
          Planifica y controla los lineamientos sociales, educacionales, culturales y demás ejes de la compañía — todos conectados a tus comunidades y compromisos.
        </p>

        {/* Infografía: hub central + ejes */}
        <div className="mt-auto relative">
          {/* Hub central decorativo */}
          <div className="flex justify-center mb-6">
            <div className="rounded-full border border-brand-orange-400/40 bg-brand-orange-500/15 px-7 py-3 flex items-center gap-3">
              <Crosshair size={26} weight="duotone" className="text-brand-orange-300" />
              <span className="font-display text-xl font-bold text-white">Ejes estratégicos</span>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-4">
            {ejes.map((e) => (
              <div key={e.label} className={`rounded-2xl border ${tones[e.tone]} p-5 flex flex-col items-center gap-3 text-center min-h-[130px] justify-center`}>
                <e.icon size={38} weight="duotone" />
                <div className="text-[15px] font-semibold text-white leading-tight">{e.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </BrochureFrame>
  );
}

// =================================================================
// 04 — Alertas multicanal
// =================================================================
function BrochureAlertas() {
  const channels = [
    { icon: EnvelopeSimple, label: "Correo", tone: "orange" },
    { icon: WhatsappLogo, label: "WhatsApp", tone: "green" },
    { icon: PaperPlaneTilt, label: "Telegram", tone: "sky" },
    { icon: ChatsTeardrop, label: "SMS", tone: "violet" },
  ];
  const tones = {
    orange: "border-brand-orange-400/40 bg-brand-orange-500/15 text-brand-orange-300",
    green: "border-brand-green-400/40 bg-brand-green-500/15 text-brand-green-300",
    sky: "border-sky-400/40 bg-sky-500/15 text-sky-300",
    violet: "border-violet-400/40 bg-violet-500/15 text-violet-300",
  };
  return (
    <BrochureFrame id="b04-alertas">
      <div className="px-16 pt-[108px] pb-[160px] h-full flex flex-col">
        <Pill>Cero compromisos olvidados</Pill>
        <h2 className="font-display text-[68px] leading-[0.95] font-bold mt-10 max-w-[900px]">
          Tu equipo no <span className="text-brand-orange-400">olvida</span> porque el sistema <span className="text-brand-green-400">no olvida</span>.
        </h2>
        <p className="text-[22px] text-slate-300 mt-8 max-w-[850px] leading-snug">
          Alertas automáticas para compromisos comunitarios, RCA y proyectos — por correo, WhatsApp, Telegram o SMS, en los plazos que tu equipo defina.
        </p>
        <div className="flex-1 flex flex-col items-center justify-center mt-6">
          <div className="w-44 h-44 rounded-full bg-gradient-to-br from-brand-orange-400 to-brand-orange-600 flex items-center justify-center shadow-[0_0_100px_-5px] shadow-brand-orange-500/70 mb-6">
            <Bell size={84} weight="duotone" className="text-white" />
          </div>
          <div className="grid grid-cols-4 gap-5 w-full mt-2">
            {channels.map((c) => (
              <div key={c.label} className={`rounded-2xl border ${tones[c.tone]} p-6 flex flex-col items-center gap-3`}>
                <c.icon size={44} weight="duotone" />
                <div className="text-lg font-bold text-white">{c.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </BrochureFrame>
  );
}

// =================================================================
// 05 — Seguridad AWS
// =================================================================
function BrochureSeguridad() {
  return (
    <BrochureFrame id="b05-seguridad">
      <div className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full bg-brand-orange-500/20 blur-3xl" />
      <div className="px-16 pt-[108px] pb-[160px] h-full flex flex-col">
        <Pill>Auditable, blindado</Pill>
        <h2 className="font-display text-[72px] leading-[0.95] font-bold mt-10 max-w-[900px]">
          Tus datos socioambientales, <span className="text-brand-orange-400">a salvo</span>.
        </h2>
        <p className="text-[22px] text-slate-300 mt-8 max-w-[820px] leading-snug">
          Infraestructura AWS — la misma que confían bancos y gobiernos. Cifrada, aislada por organización y respaldada 24/7.
        </p>
        <div className="grid grid-cols-12 gap-7 mt-auto pb-4 items-center">
          <div className="col-span-5">
            <div className="rounded-2xl border-2 border-brand-orange-400/40 bg-brand-orange-500/15 p-8 text-center">
              <CloudArrowUp size={100} weight="duotone" className="text-brand-orange-400 mx-auto mb-4" />
              <div className="font-display text-3xl font-bold">Amazon Web Services</div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-brand-orange-300 mt-2">Cloud líder mundial</div>
            </div>
          </div>
          <div className="col-span-7 space-y-3">
            {[
              { icon: LockKey, t: "Cifrado extremo a extremo" },
              { icon: ShieldCheck, t: "Aislamiento multi-tenant" },
              { icon: Eye, t: "Logs de auditoría completos" },
              { icon: CloudArrowUp, t: "Backups automáticos 24/7" },
              { icon: Database, t: "99.999% durabilidad" },
            ].map((r) => (
              <div key={r.t} className="flex items-center gap-4 rounded-xl border border-white/10 bg-white/[0.04] px-5 py-4">
                <div className="w-11 h-11 rounded-lg bg-brand-orange-500/20 border border-brand-orange-400/30 flex items-center justify-center">
                  <r.icon size={22} weight="duotone" className="text-brand-orange-300" />
                </div>
                <span className="text-[17px] text-slate-100">{r.t}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </BrochureFrame>
  );
}

// =================================================================
// 06 — Stats / Resultados (big numbers)
// =================================================================
function BrochureStats() {
  return (
    <BrochureFrame id="b06-stats" bg="dark">
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 left-0 w-full h-1/2 bg-gradient-to-b from-brand-orange-500/20 to-transparent" />
      </div>
      <div className="px-16 pt-[108px] pb-[160px] h-full flex flex-col">
        <Pill>Resultados desde el día 1</Pill>
        <h2 className="font-display text-[72px] leading-[0.95] font-bold mt-10 max-w-[900px]">
          Presenta en el directorio con <span className="text-brand-orange-400">datos en línea</span>, no con planillas.
        </h2>
        <p className="text-[22px] text-slate-300 mt-8 max-w-[850px] leading-snug">
          Trazabilidad total de tu gestión social y ambiental, plazos al día y reportes ejecutivos listos en un click.
        </p>
        <div className="grid grid-cols-2 gap-6 mt-auto pb-4">
          {[
            { stat: "100%", label: "Trazabilidad", sub: "Resiste cualquier auditoría con evidencia y autor.", tone: "orange" },
            { stat: "0", label: "Multas por olvidos", sub: "Alertas que llegan antes del vencimiento.", tone: "green" },
            { stat: "1 click", label: "Reporte al directorio", sub: "PDF y Excel listos sin pedirle nada a TI.", tone: "sky" },
            { stat: "10×", label: "Tiempo ganado", sub: "Tu equipo gestiona, no busca archivos.", tone: "violet" },
          ].map((s) => {
            const tones = {
              orange: { border: "border-brand-orange-400/30", bg: "from-brand-orange-500/15", stat: "text-brand-orange-300" },
              green: { border: "border-brand-green-400/30", bg: "from-brand-green-500/15", stat: "text-brand-green-300" },
              sky: { border: "border-sky-400/30", bg: "from-sky-500/15", stat: "text-sky-300" },
              violet: { border: "border-violet-400/30", bg: "from-violet-500/15", stat: "text-violet-300" },
            }[s.tone];
            return (
              <div key={s.label} className={`rounded-2xl border ${tones.border} bg-gradient-to-br ${tones.bg} to-transparent p-7 flex flex-col`}>
                <div className={`font-display text-7xl font-bold ${tones.stat} leading-none`}>{s.stat}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-white mt-4 font-bold">{s.label}</div>
                <div className="text-[15px] text-slate-400 mt-3 leading-relaxed">{s.sub}</div>
              </div>
            );
          })}
        </div>
      </div>
    </BrochureFrame>
  );
}

// =================================================================
// 07 — Cómo funciona (4 pasos)
// =================================================================
function BrochureComoFunciona() {
  const steps = [
    { n: "1", t: "Registra", d: "Comunidades, autoridades, gremios.", icon: Users },
    { n: "2", t: "Crea", d: "Compromisos, proyectos, RCA.", icon: ListChecks },
    { n: "3", t: "Documenta", d: "Actas y evidencias en cloud.", icon: ClipboardText },
    { n: "4", t: "Mide", d: "Alertas, dashboard, reportes.", icon: ChartLineUp },
  ];
  return (
    <BrochureFrame id="b07-flow">
      <div className="px-16 pt-[108px] pb-[160px] h-full flex flex-col">
        <Pill>Implementación rápida</Pill>
        <h2 className="font-display text-[72px] leading-[0.95] font-bold mt-10 max-w-[900px]">
          Operativa en <span className="text-brand-orange-400">días</span>, no en meses.
        </h2>
        <p className="text-[22px] text-slate-300 mt-8 max-w-[850px] leading-snug">
          Migramos tu Excel de comunidades y RCA, capacitamos a tu equipo y empiezas a ganar control desde la primera semana.
        </p>
        <div className="grid grid-cols-4 gap-6 mt-auto pb-6 relative">
          <div className="absolute top-[75px] left-[10%] right-[10%] h-px bg-gradient-to-r from-brand-orange-500 via-brand-green-500 to-sky-500 opacity-40" />
          {steps.map((s) => (
            <div key={s.n} className="relative flex flex-col items-center text-center">
              <div className="w-[150px] h-[150px] rounded-full bg-slate-900 border-2 border-brand-orange-400/50 flex items-center justify-center relative z-10">
                <s.icon size={56} weight="duotone" className="text-brand-orange-400" />
                <div className="absolute -top-2 -right-2 w-11 h-11 rounded-full bg-brand-orange-500 text-white font-display font-bold flex items-center justify-center text-lg shadow-lg shadow-brand-orange-500/40">{s.n}</div>
              </div>
              <h4 className="font-display text-2xl font-bold mt-6 mb-2">{s.t}</h4>
              <p className="text-[15px] text-slate-400">{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </BrochureFrame>
  );
}

// =================================================================
// 08 — CTA / Cierre
// =================================================================
function BrochureCTA() {
  return (
    <BrochureFrame id="b08-cta" bg="orange">
      <div className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full bg-white/15 blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full bg-amber-300/30 blur-3xl" />
      <div className="px-16 pt-[108px] pb-[160px] h-full flex flex-col relative">
        <Pill tone="white">Demo · 30 minutos · Sin compromiso</Pill>
        <h2 className="font-display text-[88px] leading-[0.9] font-bold mt-8 tracking-tight">
          30 minutos<br />pueden <span className="underline decoration-white/40 decoration-[8px] underline-offset-8">cambiar</span><br />tu gestión.
        </h2>
        <p className="text-[22px] text-white/95 mt-8 max-w-[820px] leading-snug">
          Te mostramos SIGSAOP sin compromiso, sin instalaciones.
        </p>
        {/* 3-step infographic */}
        <div className="grid grid-cols-3 gap-4 mt-auto">
          {[
            { n: "1", t: "Conversamos", d: "Entendemos tu contexto." },
            { n: "2", t: "Configuramos", d: "Adaptamos a tus ejes." },
            { n: "3", t: "Te mostramos", d: "Demo con datos." },
          ].map((s) => (
            <div key={s.n} className="rounded-2xl border border-white/25 bg-white/10 backdrop-blur-sm p-5">
              <div className="w-12 h-12 rounded-full bg-white text-brand-orange-600 font-display font-bold flex items-center justify-center text-2xl">{s.n}</div>
              <div className="font-display text-2xl font-bold mt-4 text-white">{s.t}</div>
              <div className="text-[14px] text-white/85 mt-1">{s.d}</div>
            </div>
          ))}
        </div>
      </div>
    </BrochureFrame>
  );
}

// =================================================================
// Main Page
// =================================================================
const BROCHURES = [
  { Component: BrochureCover, title: "01 · Portada" },
  { Component: BrochureAntofagasta, title: "02 · De Antofagasta al mundo" },
  { Component: BrochureExperiencia, title: "03 · Sabemos gestionar comunidades" },
  { Component: BrochureProblema, title: "04 · El costo de seguir igual" },
  { Component: BrochureModulos, title: "05 · 10 módulos" },
  { Component: BrochureMapa, title: "06 · Mapa territorial" },
  { Component: BrochureDashboard, title: "07 · Dashboard ejecutivo" },
  { Component: BrochureProyectos, title: "08 · Proyectos" },
  { Component: BrochureMetasCorporativas, title: "09 · Metas corporativas" },
  { Component: BrochureComoFunciona, title: "10 · Operativa en días" },
  { Component: BrochureAlertas, title: "11 · Alertas multicanal" },
  { Component: BrochureSeguridad, title: "12 · Seguridad AWS" },
  { Component: BrochureStats, title: "13 · Resultados" },
  { Component: BrochureCTA, title: "14 · CTA" },
];

export default function Brochures() {
  return (
    <div className="min-h-screen bg-slate-100 text-slate-900 print-page">
      <header className="bg-white border-b border-slate-200 shadow-sm print:hidden">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-2xl font-bold">SIGSAOP · Brochures para LinkedIn</h1>
            <p className="text-sm text-slate-500">14 piezas 1080×1080 — listas para carrusel.</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => window.print()}
              className="rounded-full bg-brand-orange-500 hover:bg-brand-orange-600 text-white font-bold px-5 py-2.5 text-sm transition shadow-lg shadow-brand-orange-500/30"
              data-testid="brochures-print-btn"
            >
              Imprimir / Guardar PDF
            </button>
            <Link
              to="/presentacion"
              className="text-sm text-slate-600 hover:text-slate-900 transition"
            >
              ← Volver al deck
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-[1180px] mx-auto px-6 py-10 space-y-12 print:space-y-0 print:p-0 print:max-w-none">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-600 print:hidden">
          <strong className="text-slate-900 font-bold">Cómo usarlas en LinkedIn:</strong>
          <ol className="list-decimal pl-5 mt-3 space-y-1">
            <li>Toma un screenshot de cada brochure (cada uno mide 1080×1080).</li>
            <li>O imprime esta página como PDF y súbelo como <em>Document post</em> (carrusel nativo de LinkedIn).</li>
            <li>Recomendado: usar las 8 piezas como carrusel en orden — máxima retención de scroll.</li>
          </ol>
        </div>

        {BROCHURES.map(({ Component, title }, i) => (
          <section key={title} className="print:break-after-page print:m-0">
            <div className="flex items-center justify-between mb-4 print:hidden">
              <h3 className="font-display text-lg font-bold text-slate-800">{title}</h3>
              <span className="text-xs uppercase tracking-wider text-slate-400">{i + 1} / {BROCHURES.length}</span>
            </div>
            <div className="flex justify-center">
              <div
                className="print:scale-100 print:!w-auto print:!h-auto"
                style={{ width: 1080 * 0.55, height: 1080 * 0.55, overflow: "hidden" }}
                data-testid={`brochure-wrapper-${i + 1}`}
              >
                <div style={{ transform: "scale(0.55)", transformOrigin: "top left" }}>
                  <Component />
                </div>
              </div>
            </div>
          </section>
        ))}
      </div>

      <style>{`
        @media print {
          @page { size: 1080px 1080px; margin: 0; }
          body, html { background: white !important; }
          .print-page { background: white !important; }
        }
        .font-display { font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif; }
      `}</style>
    </div>
  );
}
